import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {TotalPriceSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/basket_sections.types';
import {useIsStorageConsentGiven} from '@aboutyou/storage';
import {
  COLORS,
  Divider,
  HorizontalLogoList,
  Paper,
  Z_INDEXES,
  mobileOnlyLinaria,
  tabletAndUpLinaria,
  useIsomorphicLayoutEffect,
} from '@aboutyou/ui-components';
import {useCustomerId} from '@aboutyou/user';
import {isCaseTypeCheck} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {FC, useEffect, useRef, useState} from 'react';
import {useRect} from 'react-viewport-utils';
import {useAuthenticationPrompt} from '../../../features/Authentication/AuthenticationProvider/useAuthenticationPrompt';
import {useIsAuthenticated} from '../../../features/Authentication/hooks/useIsAuthenticated';
import {useGoToCheckoutCallback} from '../../../features/Checkout/useGoToCheckoutCallback';
import {PayPalCheckoutButtonWrapper} from '../../../features/ExpressCheckout/PayPalCheckoutButtonWrapper';
import {PriceSummaryRow} from '../../../features/PriceSummaryRow/PriceSummaryRow';
import {ExpressCheckout} from '../ExpressCheckout/ExpressCheckout';
import {BasketTotalCoinsRow} from './Components/BasketTotalCoinsRow';
import {BasketTotalDiscountCard} from './Components/BasketTotalDiscountCard';
import {CampaignOverlay} from './Components/CampaignOverlay';
import {CheckoutTotal} from './Components/CheckoutTotal';
import {GoToCheckoutButton} from './Components/GoToCheckoutButton';
import {LogInHintRow} from './Components/LogInHintRow';

export interface BasketTotalViewProps {
  checkoutSource: string;
  className?: string;
  withTileWrapper?: boolean;
  withDesktopTeaser?: boolean;
  withDiscountCard?: boolean;
  onExpressPaymentEnd?: VoidFunction;
  onExpressPaymentStart?: VoidFunction;
  onStickyHeightChange?: (height: number) => void;
}

type BasketTotalViewInnerProps = TotalPriceSection & BasketTotalViewProps;

const BasketTotalViewInner: FC<BasketTotalViewInnerProps> = ({
  checkoutSource,
  withDesktopTeaser = false,
  type,
  onExpressPaymentEnd,
  onExpressPaymentStart,
  basketPriceSummary,
  onStickyHeightChange,
}) => {
  const [overlay, setOverlay] = useState(false);
  const [loading, setLoading] = useState(false);
  const goToCheckoutCallback = useGoToCheckoutCallback();
  const customerId = useCustomerId();
  const {isAuthenticated} = useIsAuthenticated();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const consentAccepted = useIsStorageConsentGiven();
  const checkoutButtonsRef = useRef<HTMLDivElement>(null);
  const stickyRowRef = useRef<HTMLDivElement>(null);
  const checkoutButtonsRect = useRect(checkoutButtonsRef, {
    disableScrollUpdates: true,
    priority: 'high',
  });

  useIsomorphicLayoutEffect(() => {
    onStickyHeightChange?.(
      (stickyRowRef.current?.clientHeight || 0) +
        (checkoutButtonsRect?.height - 1 || 0),
    );
  }, [checkoutButtonsRect, stickyRowRef, onStickyHeightChange]);

  const {isActive: showLogInHint} = useIsFeatureActive(
    'show_basket_login_hint',
    {abTest: true},
  );

  const {isActive: isCoinsActive} = useIsFeatureActive('ff_coins_rollout');

  const {
    isActive: isExpressCheckoutActive,
    variant: variantExpressCheckout,
    activateAbTest,
  } = useIsFeatureActive('basket_v2_express_checkout');

  const {isActive: isGuestCheckoutActive} = useIsFeatureActive(
    'enable_guest_checkout',
  );

  useEffect(() => {
    if (
      variantExpressCheckout === 'control-desktop' ||
      variantExpressCheckout === 'control-mweb'
    ) {
      activateAbTest();
    }
  }, [variantExpressCheckout, activateAbTest]);

  if (!isCaseTypeCheck('totalPriceInfo', type) || !type.totalPriceInfo) {
    return null;
  }

  const {
    totalPrice,
    paymentMethods,
    showAppInstallTeaser,
    expressCheckoutProviders,
  } = type.totalPriceInfo;

  const [paypalConfig] = (expressCheckoutProviders || []).filter(
    provider => provider.type?.$case === 'paypal',
  );

  const basketId = paypalConfig?.type?.paypal.basketId;

  const onGoToCheckout = () => {
    setLoading(true);
    setOverlay(false);

    showUserAuthenticationPrompt({
      context: 'basket-page-go-to-checkout',
      needsCheckoutSession: true,
      allowGuestLogin: isGuestCheckoutActive,
      onCancel: () => setLoading(false),
      forceRedirectToCheckoutOnSSOLoginSuccess: true,
      onSuccess: () => {
        goToCheckoutCallback();
      },
    });
  };

  return (
    <>
      {!isAuthenticated && showLogInHint && (
        <>
          <LogInHintRow />
          <StyledDivider style={{marginBottom: '24px'}} />
        </>
      )}

      {overlay && (
        <CampaignOverlay
          onTimeElapsed={onGoToCheckout}
          onCancel={() => setOverlay(false)}
          onButtonClick={onGoToCheckout}
          withDesktopTeaser={withDesktopTeaser}
          progressDuration={7000}
        />
      )}

      {basketPriceSummary.rows.length && (
        <PriceSummaryRows data-testid="rows">
          {basketPriceSummary.rows.map((row, index) => (
            <StyledPriceSummaryRow key={index} {...row} />
          ))}
        </PriceSummaryRows>
      )}
      <Sticky
        ref={stickyRowRef}
        style={{
          bottom: checkoutButtonsRect?.height
            ? checkoutButtonsRect.height - 1
            : 0,
        }}
      >
        {basketPriceSummary.stickyRows.length > 0 && (
          <StickyPriceSummaryRows data-testid="stickyRows">
            {basketPriceSummary.stickyRows.map((row, index) => (
              <StyledPriceSummaryRow key={index} {...row} />
            ))}
          </StickyPriceSummaryRows>
        )}

        <StyledCheckoutTotal {...basketPriceSummary.checkoutTotal} />
      </Sticky>
      {isCoinsActive && basketPriceSummary.coinsRow && (
        <StyledBasketTotalCoinsRow
          title={basketPriceSummary.coinsRow.title}
          amount={basketPriceSummary.coinsRow.value}
        />
      )}
      <CheckoutButtonWrapper ref={checkoutButtonsRef}>
        <StyledGoToCheckoutButton
          onClick={() =>
            showAppInstallTeaser ? setOverlay(true) : onGoToCheckout()
          }
          isLoading={loading}
        />
        <ExpressCheckout basketTotal={totalPrice.checkoutTotal} />
      </CheckoutButtonWrapper>

      {!!basketId && isExpressCheckoutActive && consentAccepted && (
        <StyledPayPalCheckoutButtonWrapper
          source={checkoutSource}
          basketId={basketId}
          totalPrice={totalPrice.withTax}
          customerId={customerId}
          onExpressPaymentEnd={onExpressPaymentEnd}
          onExpressPaymentStart={onExpressPaymentStart}
        />
      )}

      <StyledDivider />

      {paymentMethods.length > 0 && (
        <HorizontalLogoList
          items={paymentMethods}
          padded
          withBorder
          dataTestId="basketPaymentMethods"
        />
      )}
    </>
  );
};

const withTileWrapper = (
  InnerComponent: React.ComponentType<BasketTotalViewInnerProps>,
) => {
  const WrappedComponent: React.FC<BasketTotalViewInnerProps> = props => {
    if (!props.totalPrice?.withTax) {
      return null;
    }

    if (!props.withTileWrapper) {
      return <InnerComponent {...props} />;
    }

    return (
      <>
        {props.withDiscountCard && <BasketTotalDiscountCard />}
        <StyledPaper>
          <InnerComponent {...props} />
        </StyledPaper>
      </>
    );
  };

  return WrappedComponent;
};

export const BasketTotalView = withTileWrapper(BasketTotalViewInner);

const Sticky = styled.div`
  padding-top: 8px;

  ${mobileOnlyLinaria(`
    position: sticky;
    bottom: 0;
    z-index: ${Z_INDEXES.ONE};
    background: ${COLORS.White};
    padding: 8px 16px 0 16px;
  `)}
`;

const CheckoutButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 8px;

  ${mobileOnlyLinaria(`
    position: sticky;
    bottom: 0;
    z-index: ${Z_INDEXES.ONE};
    background: ${COLORS.White};
    padding: 0px 16px 16px 16px;
  `)}
`;

const StyledBasketTotalCoinsRow = styled(BasketTotalCoinsRow)`
  margin: 8px 16px;

  ${tabletAndUpLinaria(`
    margin: 8px 0;
  `)}
`;

const StyledPaper = styled(Paper)`
  padding: 24px 24px 0 24px;
`;

const StyledDivider = styled(Divider)`
  margin: 24px 0 0 0;

  ${tabletAndUpLinaria(`
    margin: 24px -24px 0 -24px;
    width: calc(100% + 48px);
  `)}
`;

const priceSummaryRowsStyles = `
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PriceSummaryRows = styled.div`
  ${priceSummaryRowsStyles}

  ${mobileOnlyLinaria(`
    padding: 16px 16px 0 16px;
  `)}
`;

const StickyPriceSummaryRows = styled.section`
  ${priceSummaryRowsStyles}
`;

const StyledCheckoutTotal = styled(CheckoutTotal)`
  margin-top: 8px;
`;

const StyledGoToCheckoutButton = styled(GoToCheckoutButton)`
  &&& {
    margin-top: 8px;
  }
`;

const StyledPayPalCheckoutButtonWrapper = styled(PayPalCheckoutButtonWrapper)`
  margin-top: 8px;

  ${mobileOnlyLinaria(`
    margin-top: 0;
    padding: 0 16px;
  `)}
`;

const StyledPriceSummaryRow = styled(PriceSummaryRow)`
  padding: 2px 8px;
`;
